import React, { useState, useCallback } from 'react';
import { Col, Container, Row } from 'reactstrap';
import { DashboardPageHeader } from '../../Components/Common/DashboardKit';
import 'react-toastify/dist/ReactToastify.css';

// Round ORG-UAT-1 (2026) — THREE selectable chart styles with a persistent
// per-user default, per UAT request:
//   • Team    — drill-down card grid (chartContent, viewMode="team")
//   • Tree    — classic top-down connected chart (chartContent, viewMode="tree")
//   • Compact — d3-org-chart compact/stacked layout (OrgChartPro)
// The user picks a style and can "Set as default"; the choice is saved to
// localStorage (org.defaultView) and restored on next visit.
//
// Round ORG-2 (2026) — added the HR-only Data Quality panel toggle (Issue 6,
// legacy OrgChart.vb parity: missing photos/titles/dates/extensions +
// anniversaries, extended with supervisor-chain checks). The button renders
// only for HR users; the API behind the panel is ALSO gated server-side
// (CanAccessHR policy), so hiding the button is convenience, not security.
// Everything else on this page is unchanged.
import ChartContent from './chartContent';
import OrgChartPro from './OrgChartPro';
import OrgDataQualityPanel from './OrgDataQualityPanel';
import { usePermissions } from '../../hooks/usePermissions';

const VIEW_OPTIONS = [
    { key: "team", label: "Team View", icon: "ri-group-line", hint: "Drill into one manager's reports at a time" },
    { key: "tree", label: "Tree View", icon: "ri-git-merge-line", hint: "Classic top-down connected chart" },
    { key: "compact", label: "Compact View", icon: "ri-layout-grid-line", hint: "Space-saving stacked layout" },
];

const DEFAULT_KEY = "org.defaultView";

const Shukla = () => {
    document.title = "SSW Technologies Dashboard";

    const readDefault = () => {
        try {
            const v = window.localStorage.getItem(DEFAULT_KEY);
            if (v && VIEW_OPTIONS.some((o) => o.key === v)) return v;
        } catch (_) { /* ignore */ }
        return "team";
    };

    const [activeView, setActiveView] = useState(readDefault);
    const [defaultView, setDefaultView] = useState(readDefault);
    const [savedFlash, setSavedFlash] = useState(false);
    const [showDataQuality, setShowDataQuality] = useState(false);

    const { canAccessModule } = usePermissions();
    // Frontend visibility only — GET /api/OrgChart/data-quality enforces the
    // CanAccessHR policy server-side regardless of this flag.
    const isHr = canAccessModule ? canAccessModule("hr.menu") : false;

    const setAsDefault = useCallback(() => {
        try { window.localStorage.setItem(DEFAULT_KEY, activeView); } catch (_) { /* ignore */ }
        setDefaultView(activeView);
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 1800);
    }, [activeView]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <DashboardPageHeader
                        icon="ri-organization-chart"
                        eyebrow="Company"
                        title="Organizational Chart"
                        subtitle="Explore the company's reporting structure and team composition."
                        tone="violet"
                        breadcrumb={[
                            { label: "Dashboard", to: "/" },
                            { label: "Workflows", to: "/workflows" },
                            { label: "Organizational Chart" },
                        ]}
                    />

                    {/* View switcher */}
                    <div className="org-view-switcher">
                        <div className="org-view-seg">
                            {VIEW_OPTIONS.map((o) => (
                                <button
                                    key={o.key}
                                    type="button"
                                    className={`org-view-btn ${activeView === o.key ? "active" : ""}`}
                                    onClick={() => setActiveView(o.key)}
                                    title={o.hint}
                                >
                                    <i className={o.icon} />
                                    <span>{o.label}</span>
                                    {defaultView === o.key && (
                                        <i className="ri-star-fill org-view-star" title="Your default view" />
                                    )}
                                </button>
                            ))}
                        </div>

                        <div className="org-view-default">
                            {defaultView === activeView ? (
                                <span className="org-view-isdefault">
                                    <i className="ri-star-fill" /> This is your default
                                </span>
                            ) : (
                                <button type="button" className="org-view-setdefault" onClick={setAsDefault}>
                                    <i className="ri-star-line" /> Set as default
                                </button>
                            )}
                            {savedFlash && <span className="org-view-saved">Saved &#10003;</span>}

                            {/* Round ORG-2 — HR-only Data Quality toggle */}
                            {isHr && (
                                <button
                                    type="button"
                                    className={`org-view-setdefault ms-2 ${showDataQuality ? "active" : ""}`}
                                    onClick={() => setShowDataQuality((v) => !v)}
                                    title="Missing photos/titles/dates/extensions, orphaned chains, and upcoming anniversaries (HR only)"
                                >
                                    <i className="ri-shield-check-line" /> Data Quality
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Round ORG-2 — HR-only data-quality panel (server-gated API) */}
                    {isHr && showDataQuality && (
                        <Row>
                            <Col>
                                <OrgDataQualityPanel />
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col>
                            {activeView === "compact" ? (
                                <OrgChartPro key="compact" canEditInHr={isHr} />
                            ) : (
                                <ChartContent
                                    key={activeView}
                                    initialViewMode={activeView}
                                    canEditInHr={isHr}
                                />
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Shukla;
